import type { ReactNode } from 'react'
import { Check, Pencil } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type Props = {
  testId: string
  index: number
  title: string
  /** One line saying what will happen, shown once the block is confirmed. */
  summary: ReactNode
  confirmed: boolean
  open: boolean
  locked?: boolean
  confirmLabel: string
  confirmDisabled?: boolean
  onConfirm: () => void
  onEdit: () => void
  children: ReactNode
}

/**
 * One decision of the review. Open: the choices. Confirmed: folds to a single green line, so the
 * page empties as the user moves through it (the calm cockpit rule: done fades, what is left stands out).
 */
export function ReviewBlockCard({
  testId, index, title, summary, confirmed, open, locked, confirmLabel, confirmDisabled, onConfirm, onEdit, children,
}: Props) {
  return (
    <section
      data-testid={testId}
      data-state={confirmed ? 'confirmed' : open ? 'open' : 'pending'}
      className={cn(
        'rounded-xl border bg-background transition-shadow',
        open && !confirmed && 'shadow-sm ring-1 ring-slate-900/10',
        confirmed && 'border-emerald-200 bg-emerald-50/40'
      )}
    >
      <header className="flex items-center gap-3 px-5 py-4">
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-medium',
            confirmed ? 'bg-emerald-500 text-white' : open ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'
          )}
        >
          {confirmed ? <Check className="h-4 w-4" /> : index}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold">{title}</h3>
          {(confirmed || !open) && <div className="truncate text-sm text-muted-foreground">{summary}</div>}
        </div>
        {confirmed && !locked && (
          <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`${testId}-edit-btn`} className="gap-1.5">
            <Pencil className="h-3.5 w-3.5" />
            Modifier
          </Button>
        )}
        {!confirmed && !open && <span className="text-xs text-muted-foreground">À vérifier</span>}
      </header>

      {open && !confirmed && (
        <div className="space-y-5 border-t px-5 pb-5 pt-4">
          {children}
          <div className="flex justify-end">
            <Button onClick={onConfirm} disabled={confirmDisabled} data-testid={`${testId}-confirm-btn`} className="gap-2">
              <Check className="h-4 w-4" />
              {confirmLabel}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
